import * as React from 'react';
import classnames from 'classnames';

import { Res } from 'core/api';
import { cloneExcept } from 'core/utils';

import { Constraint, check } from './Constraints';
import { FormComponent } from './Form';
import { whenEnterKey, formatAsElementId, Format, formatValue, supportSelection } from './Utils';
import ErrorBlock from './ErrorBlock';

import { Binding, Result } from 'core/infrastructure';

import { startSyncing, stopSyncing } from './TextBox.syncTextFrom';
import Button, { ButtonStyle } from './Button';

interface Props<T> extends React.DetailedHTMLProps<React.InputHTMLAttributes<TextBox<T>>, TextBox<T>> {
	linkTo: Binding.PropertyPath<T>;
	propertyType?: Binding.PropertyType;
	label?: string | React.ReactNode;
	rules?: Constraint | Constraint[];
	lines?: number;
	format?: Format;
	onEnterPress?: () => void;
	onValueChange?: (value: string) => void;
	boundValue?: any;
	syncTextFrom?: string; // Propiedad enlazada a otro control que queremos sincronizar
	formId?: string;
	childrenPosition?: 'append' | 'prepend'
}

interface State {
	value?: string;
	errorMsg?: string;
}

function getInputTypeFromPropertyType(propertyType: Binding.PropertyType) {
	switch (propertyType) {
		case 'int':
			return 'number';
		case 'decimal':
			return 'number';
		default:
			return 'text';
	}
}

export class TextBox<T> extends React.Component<Props<T>, State> implements FormComponent {

	private input = React.createRef<HTMLInputElement>();

	constructor(props: Props<T>) {
		super(props);

		this.state = this.createState(props.boundValue);
	}

	private createState(value: any): State {
		return {
			value: formatValue(value, this.props.format),
			errorMsg: ''
		};
	}

	getValue(): Result<any> {

		this.setState({ errorMsg: '' });
		const value = this.state.value;

		if (this.props.required && (!value || !value.trim())) {
			this.setState({ errorMsg: Res.Msg.MSG_VALUE_REQUIRED() });
			return Result.error();
		}

		const result = Binding.parse(this.state.value, this.props.propertyType || 'string', !!this.props.required);

		if (result.errorMsg) {
			this.setState({ errorMsg: result.errorMsg });
			return Result.error();
		}

		const rulesError = this.props.rules && check(this.props.rules, result.value);

		if (rulesError) {
			this.setState({ errorMsg: rulesError });
			return Result.error();
		}

		return Result.ok(result.value);
	}

	setValue(value: any) {
		this.setState(this.createState(value));
	}

	activate() {

		this.input.current!.focus();
		if (supportSelection(this.input.current!))
			this.input.current!.select();
	}

	focus() {
		this.input.current?.focus();
	}

	clear() {
		this.setState({ value: '', errorMsg: '' });
	}

	getPath(): string {
		return Binding.propertyPathToString(this.props.linkTo);
	}

	showErrors(errors: string[]): void {
		this.setState({ errorMsg: errors.join(' ') });
	}

	componentDidMount() {
		if (this.props.syncTextFrom)
			startSyncing('#' + formatAsElementId(this.props.syncTextFrom, this.props.formId || ''), '#' + formatAsElementId(this.getPath(), this.props.formId || ''));
	}

	componentWillUnmount() {
		if (this.props.syncTextFrom)
			stopSyncing('#' + formatAsElementId(this.props.syncTextFrom, this.props.formId || ''), '#' + formatAsElementId(this.getPath(), this.props.formId || ''));
	}

	buildInputGroup(input: React.ReactNode, children: React.ReactElement<any>[], childrenPosition: 'append' | 'prepend') {
		// OJO: Esto sólo funciona si los children son botones o span. Si queremos soportar
		// más cosas deberíamos analizar el tipo de Children para ver qué hacemos con cada uno.

		if (children.length === 0)
			return input;

		if (children.some(x => x.type === 'span'))
			return <div className='input-group'>
				{childrenPosition === 'prepend' && children}
				{input}
				{childrenPosition === 'append' && children}
			</div>;

		return <div className='input-group'>
			{childrenPosition === 'prepend' && <span className='input-group-btn'>{children}</span>}
			{input}
			{childrenPosition === 'append' && <span className='input-group-btn'>{children}</span>}
		</div>;
	}

	render() {

		const inputId = formatAsElementId(this.getPath(), this.props.formId || '');
		const props: any = cloneExcept(this.props, ['linkTo', 'boundValue', 'propertyType', 'label', 'rules', 'lines', 'onEnterPress', 'children', 'syncTextFrom', 'onValueChange', 'ref', 'formId', 'format', 'childrenPosition']);

		const onChange = (e: any) => {
			const value = e.target.value;
			this.setState({ value: value }, () => this.props.onValueChange && this.props.onValueChange(value));
		};

		const onKeyDown = this.props.onEnterPress && whenEnterKey(this.props.onEnterPress);
		const onFocus = (e: React.FocusEvent<HTMLInputElement>) => {
			if (!supportSelection(e.target))
				return;

			const selectionStart = e.target.selectionStart || 0;
			const selectionEnd = e.target.selectionEnd || 0;

			if (selectionEnd - selectionStart === 0) {
				e.target.select();
			}
		};

		props.type = props.type || getInputTypeFromPropertyType(this.props.propertyType || 'string');

		props.className = (props.className || '') + ' form-control';
		if (props.autoFocus)
			props.className += ' auto-focus';

		if (props.type === 'number')
			props.step = 'any';

		const input = this.props.lines || 1 > 1
			? <textarea id={inputId} ref={this.input} rows={this.props.lines} {...props}
				value={this.state.value} onChange={onChange} onKeyDown={onKeyDown} />
			: <input id={inputId} ref={this.input}
				value={this.state.value} onChange={onChange} onKeyDown={onKeyDown} onFocus={onFocus} {...props} />;


		const children = React.Children.map(this.props.children, (c: any) => React.cloneElement(c, { disabled: this.props.disabled })) || [];

		const inputGroup = this.buildInputGroup(input, children, this.props.childrenPosition || 'append');

		const label = this.props.label
			? <label className='col-sm-4 control-label' htmlFor={inputId}>
				{this.props.label || ''}
			</label>
			: null;

		const divInputClasses = classnames({ 'col-sm-8': !!label, 'col-sm-12': !label });

		return <div className={classnames({ 'form-group': true, 'has-error': !!this.state.errorMsg })}>

			{label}

			<div className={divInputClasses}>
				{inputGroup}
				<ErrorBlock msg={this.state.errorMsg} />
			</div>

		</div>;
	}
}

export const TextBoxButton = (props: React.ButtonHTMLAttributes<HTMLButtonElement> & { iconClassName?: string, text?: string, buttonStyle?: ButtonStyle, outline?: boolean }) => {

	// Por defecto pongo un btn-default. Si me llega el className otra cosa, se sobreescribirá
	// (aunque no sea de la forma más eficiente porque en realidad se aplican ambas clases)
	const className = 'btn ' + (props.className || '');

	const { iconClassName, buttonStyle, outline, ...buttonProps } = cloneExcept(props, ['model', 'className']);

	// Construyo el contenido del botón a partir del icono, texto e hijos recibidos
	let content: any[] = [];

	if (iconClassName) {
		content.push(<span key='icon' className={iconClassName}></span>);
	}

	if (props.text) {
		content.push(<span key='text'>text</span>);
	}

	content = content.concat(props.children);
	return <Button outline={outline} buttonStyle={buttonStyle} {...buttonProps} className={className} disabled={props.disabled}>{content}</Button>;
};


export type TextBoxCtor<T> = new (props: Props<T>) => TextBox<T>;
